import { useRef, useEffect } from 'react';

const useClickAndDragScroll = () => {
  const ref = useRef(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);
  const hasDragged = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseDown = (e) => {
      // Linke Maustaste noetig
      if (e.button !== 0) return;
      // Nicht scrollen bei clicks auf Buttons oder DnD-draggable Elemente (Tickets),
      // sonst scrollt der Container waehrend des Drag-and-Drop -> Drop-Zonen verschoben
      if (e.target.tagName === 'BUTTON') return;
      if (e.target.closest('[data-draggable="true"]')) return;

      isDown.current = true;
      hasDragged.current = false;
      element.style.cursor = 'grabbing';
      element.style.userSelect = 'none';

      startX.current = e.pageX - element.offsetLeft;
      startY.current = e.pageY - element.offsetTop;
      scrollLeft.current = element.scrollLeft;
      scrollTop.current = element.scrollTop;

      // Prevent default to avoid text selection
      e.preventDefault();
    };

    const handleMouseLeave = () => {
      isDown.current = false;
      element.style.cursor = 'grab';
      element.style.userSelect = 'auto';
    };

    const handleMouseUp = () => {
      isDown.current = false;
      element.style.cursor = 'grab';
      element.style.userSelect = 'auto';
    };

    const handleClick = (e) => {
      // Prevent clicks if user has dragged
      if (hasDragged.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseMove = (e) => {
      if (!isDown.current) return;
      
      e.preventDefault();
      
      const x = e.pageX - element.offsetLeft;
      const y = e.pageY - element.offsetTop;
      const walkX = (x - startX.current) * 2; // Multiply by 2 for faster scrolling
      const walkY = (y - startY.current) * 2;
      
      // If moved more than 5px, consider it a drag
      if (Math.abs(walkX) > 5 || Math.abs(walkY) > 5) {
        hasDragged.current = true;
      }
      
      element.scrollLeft = scrollLeft.current - walkX;
      element.scrollTop = scrollTop.current - walkY;
    };

    // Set initial cursor
    element.style.cursor = 'grab';

    // Add event listeners
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('click', handleClick, true); // Use capture phase

    // Cleanup
    return () => {
      if (element) {
        element.removeEventListener('mousedown', handleMouseDown);
        element.removeEventListener('mouseleave', handleMouseLeave);
        element.removeEventListener('mouseup', handleMouseUp);
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('click', handleClick, true);
        element.style.cursor = 'auto';
        element.style.userSelect = 'auto';
      }
    };
  }, []);

  return ref;
};

export default useClickAndDragScroll;